import { FORCAS_NAMES } from './flat-questions.js';

/**
 * Valores do Conselheiro que o schema guarda como String (prisma/schema.prisma,
 * seção "Conselheiro"). Um lugar só para servidor e client validarem a mesma
 * lista — e para o nome das forças e dos temas não se espalhar.
 */

/**
 * Os dez temas, na ordem das notas do diagnóstico (shared/scores.ts:
 * `temas[fi*2 + ti]`). O índice é o que se grava; o nome, só na tela.
 */
export const TEMAS = [
  'Mentalidade',
  'Estratégia',
  'Cultura',
  'Liderança',
  'Gestão',
  'Financeiro',
  'Marketing',
  'Vendas',
  'Tecnologia',
  'Inovação',
] as const;

/** Força 1–5 → nome (Fundação, Pessoas, Operação, Crescimento, Futuro). */
export function nomeDaForca(forca: number): string | null {
  return Number.isInteger(forca) && forca >= 1 && forca <= 5 ? FORCAS_NAMES[forca - 1] ?? null : null;
}

/** A força a que um tema (0–9) pertence, 1–5. */
export function forcaDoTema(tema: number): number | null {
  return Number.isInteger(tema) && tema >= 0 && tema <= 9 ? Math.floor(tema / 2) + 1 : null;
}

/** Enquadramento coerente: o tema, se houver, é da força informada. */
export function enquadramentoValido(forca: number | null | undefined, tema: number | null | undefined): boolean {
  if (forca == null && tema == null) return true;
  if (forca != null && nomeDaForca(forca) === null) return false;
  if (tema != null && forcaDoTema(tema) === null) return false;
  return forca == null || tema == null || forcaDoTema(tema) === forca;
}

/** Dez notas de 0 a 10 — o formato de `Radar.notas`. */
export function notasDeRadarValidas(notas: unknown): notas is number[] {
  return (
    Array.isArray(notas) &&
    notas.length === TEMAS.length &&
    notas.every((n) => typeof n === 'number' && Number.isFinite(n) && n >= 0 && n <= 10)
  );
}

export const PAPEIS_NA_EMPRESA = ['DONO', 'CONVIDADO', 'SOCIO', 'RESPONSAVEL'] as const;
export const PERFIS_DE_CONSELHEIRO = ['EMPRESARIO', 'EX_EXECUTIVO', 'SENIOR'] as const;
export const STATUS_DE_CONSELHEIRO = ['EM_FORMACAO', 'CERTIFICADO', 'SUSPENSO', 'DESLIGADO'] as const;
export const PAPEIS_NO_VINCULO = ['PRINCIPAL', 'CONVIDADO', 'SENIOR'] as const;
export const FAIXAS_DE_FATURAMENTO = ['ATE_1M', '1M_5M', '5M_15M', 'ACIMA_15M'] as const;
export const STATUS_DE_CICLO = ['ATIVO', 'ENCERRADO', 'RENOVADO'] as const;
export const TIPOS_DE_META = ['FINANCEIRA', 'OPERACIONAL', 'PESSOAS', 'PESSOAL'] as const;
export const TIPOS_DE_RADAR = ['PARTIDA', 'MARCO', 'FINAL'] as const;
export const TIPOS_DE_MANHA = ['S0', 'MENSAL', 'MARCO', 'FECHAMENTO'] as const;
export const FORMATOS_DE_MANHA = ['PRESENCIAL', 'ONLINE'] as const;
export const STATUS_DE_MANHA = [
  'AGENDADA',
  'PREPARACAO',
  'PAUTA_CONFIRMADA',
  'EM_ANDAMENTO',
  'REALIZADA',
  'CANCELADA',
] as const;
export const PAPEIS_NA_MANHA = ['SOCIO', 'CONSELHEIRO', 'CONVIDADO', 'COLABORADOR'] as const;
export const ORIGENS_DE_TEMA = ['EMPRESA', 'CONSELHEIRO', 'DESVIO'] as const;
export const STATUS_DE_TEMA = ['PROPOSTO', 'NA_PAUTA', 'DISCUTIDO', 'ADIADO'] as const;
export const STATUS_DE_DECISAO = ['ABERTA', 'EM_DIA', 'EM_RISCO', 'BLOQUEADA', 'CONCLUIDA', 'CANCELADA'] as const;
export const ORIGENS_DE_DECISAO = ['MANUAL', 'TRANSCRICAO'] as const;
export const FINALIDADES_DE_CONSENTIMENTO = ['OPERACAO', 'QUALIDADE', 'FORMACAO'] as const;

/** A empresa propõe no máximo 3 temas por manhã (§6.1). */
export const MAX_TEMAS_DA_EMPRESA = 3;

export type StatusDeDecisao = (typeof STATUS_DE_DECISAO)[number];

/**
 * Decisão encerrada não volta a andar: concluída e cancelada são finais. O
 * resto transita livremente — quem executa diz como está.
 */
export function transicaoDeDecisaoPermitida(de: StatusDeDecisao, para: StatusDeDecisao): boolean {
  if (de === para) return true;
  return de !== 'CONCLUIDA' && de !== 'CANCELADA';
}

/** Atrasada = prazo passou e não está encerrada. Derivado, não gravado. */
export function decisaoAtrasada(d: { status: string; prazo: Date | null }, agora = new Date()): boolean {
  if (!d.prazo || d.status === 'CONCLUIDA' || d.status === 'CANCELADA') return false;
  return d.prazo.getTime() < agora.getTime();
}

/** Primeiro dia do mês (UTC) — a `competencia` de IndicadorValor. */
export function competenciaDe(data: Date): Date {
  return new Date(Date.UTC(data.getUTCFullYear(), data.getUTCMonth(), 1));
}

/* ───────────────────────── regras do cadastro ───────────────────────── */

/**
 * DONO é quem criou a empresa no diagnóstico avulso; no Conselheiro ele conta
 * como sócio. Rebaixar um DONO a SOCIO tiraria dele o poder sobre os
 * diagnósticos que já existem — então DONO fica DONO e vale como sócio.
 */
export function ehSocio(papel: string): boolean {
  return papel === 'SOCIO' || papel === 'DONO';
}

/**
 * O papel que um membro passa a ter quando o HAOps o cadastra no Conselheiro.
 * Devolve null quando a mudança não pode ser feita por aqui: um DONO não vira
 * RESPONSAVEL (perderia a empresa que criou).
 */
export function papelAoCadastrar(atual: string | null, pedido: 'SOCIO' | 'RESPONSAVEL'): string | null {
  if (atual === null) return pedido;
  if (atual === 'DONO') return pedido === 'SOCIO' ? 'DONO' : null;
  return pedido;
}

export type MotivoVinculoRecusado =
  | 'CONSELHEIRO_INATIVO'
  | 'SEM_NDA'
  | 'SEM_CONFIRMACAO_DE_CONFLITO'
  | 'PRINCIPAL_EXIGE_CERTIFICACAO'
  | 'CERTIFICACAO_VENCIDA'
  | 'ORGANIZACAO_DIFERENTE'
  | 'SENIOR_EXIGE_PERFIL_SENIOR'
  | 'EMPRESA_JA_TEM_PRINCIPAL'
  | 'CONSELHEIRO_SEM_CAPACIDADE'
  | 'JA_VINCULADO';

export const MENSAGEM_VINCULO_RECUSADO: Record<MotivoVinculoRecusado, string> = {
  CONSELHEIRO_INATIVO: 'Conselheiro suspenso ou desligado não pode ser vinculado.',
  SEM_NDA: 'O conselheiro ainda não aceitou o NDA — é pré-requisito para acessar qualquer empresa.',
  SEM_CONFIRMACAO_DE_CONFLITO: 'Confirme que o conselheiro declarou não ter conflito com esta empresa.',
  PRINCIPAL_EXIGE_CERTIFICACAO: 'Só conselheiro certificado pode ser o Principal.',
  CERTIFICACAO_VENCIDA: 'A certificação deste conselheiro venceu: ele precisa renovar (a prova do nível seguinte) para ser Principal de uma empresa nova.',
  ORGANIZACAO_DIFERENTE: 'Este conselheiro responde a outra organização: só é Principal de empresas da organização dele. Conselheiro da rede (sem organização) atende qualquer empresa.',
  SENIOR_EXIGE_PERFIL_SENIOR: 'O papel Sênior é para conselheiros de perfil Sênior.',
  EMPRESA_JA_TEM_PRINCIPAL: 'Esta empresa já tem um Conselheiro Principal ativo. Encerre o vínculo dele antes.',
  CONSELHEIRO_SEM_CAPACIDADE: 'O conselheiro já atende o número máximo de empresas como Principal.',
  JA_VINCULADO: 'Este conselheiro já está vinculado a esta empresa.',
};

/**
 * Pode vincular? Devolve o motivo da recusa, ou null. Pura: quem chama
 * consulta o banco e passa os fatos.
 */
export function motivoParaRecusarVinculo(f: {
  papel: (typeof PAPEIS_NO_VINCULO)[number];
  conselheiro: {
    status: string;
    perfil: string;
    nda_aceito_em: Date | null;
    capacidade_empresas: number;
    certificado_em?: Date | null;
    /** Organização (Matriz ou franquia) a que ele responde; null = da rede. */
    organizacao_id?: string | null;
  };
  /** Organização da empresa; null = da rede. */
  organizacaoDaEmpresa?: string | null;
  semConflitoConfirmado: boolean;
  /** Vínculos ativos deste conselheiro como PRINCIPAL, em qualquer empresa. */
  principaisAtivosDoConselheiro: number;
  empresaTemPrincipalAtivo: boolean;
  jaVinculadoAEstaEmpresa: boolean;
  agora?: Date;
}): MotivoVinculoRecusado | null {
  const { conselheiro, papel } = f;
  if (conselheiro.status === 'SUSPENSO' || conselheiro.status === 'DESLIGADO') return 'CONSELHEIRO_INATIVO';
  if (!conselheiro.nda_aceito_em) return 'SEM_NDA';
  if (!f.semConflitoConfirmado) return 'SEM_CONFIRMACAO_DE_CONFLITO';
  if (f.jaVinculadoAEstaEmpresa) return 'JA_VINCULADO';
  if (papel === 'PRINCIPAL') {
    if (conselheiro.status !== 'CERTIFICADO') return 'PRINCIPAL_EXIGE_CERTIFICACAO';
    // Vencida e o agendador ainda não rebaixou: a validade vale antes do status.
    const vence = certificacaoVenceEm(conselheiro.certificado_em ?? null);
    if (vence && vence.getTime() <= (f.agora ?? new Date()).getTime()) return 'CERTIFICACAO_VENCIDA';
    // Conselheiro de uma organização é Principal só de empresa dela; o da rede, de qualquer uma (§8).
    const orgDoConselheiro = conselheiro.organizacao_id ?? null;
    if (orgDoConselheiro && orgDoConselheiro !== (f.organizacaoDaEmpresa ?? null)) return 'ORGANIZACAO_DIFERENTE';
    if (f.empresaTemPrincipalAtivo) return 'EMPRESA_JA_TEM_PRINCIPAL';
    if (f.principaisAtivosDoConselheiro >= conselheiro.capacidade_empresas) return 'CONSELHEIRO_SEM_CAPACIDADE';
  }
  if (papel === 'SENIOR' && conselheiro.perfil !== 'SENIOR') return 'SENIOR_EXIGE_PERFIL_SENIOR';
  return null;
}

/** CNPJ só com dígitos, ou null se não tem 14. */
export function normalizarCnpj(bruto: string | null | undefined): string | null {
  const d = (bruto ?? '').replace(/\D/g, '');
  return d.length === 14 ? d : null;
}

/* ───────────────────────── o uso, no app ───────────────────────── */

/** A certificação vale 1 ano; a renovação refaz a prova (docs/CONSELHEIRO_ANALISE.md §10.2). */
export const VALIDADE_DA_CERTIFICACAO_MESES = 12;

/** Somar meses sem pular o fim do mês: 31/01 + 1 mês = 28/02 (ou 29), não 03/03. */
function somarMeses(data: Date, meses: number): Date {
  const alvo = new Date(Date.UTC(data.getUTCFullYear(), data.getUTCMonth() + meses, 1));
  const ultimoDia = new Date(Date.UTC(alvo.getUTCFullYear(), alvo.getUTCMonth() + 1, 0)).getUTCDate();
  alvo.setUTCDate(Math.min(data.getUTCDate(), ultimoDia));
  alvo.setUTCHours(data.getUTCHours(), data.getUTCMinutes(), data.getUTCSeconds(), data.getUTCMilliseconds());
  return alvo;
}

export function certificacaoVenceEm(certificadoEm: Date | null): Date | null {
  return certificadoEm ? somarMeses(certificadoEm, VALIDADE_DA_CERTIFICACAO_MESES) : null;
}

/**
 * Em que mês do ciclo de 12 meses a empresa está: 1 no primeiro mês, 12 no
 * último. 0 antes de começar; depois do fim, fica em 12 (o ciclo é encerrado
 * ou renovado no HAOps, não aqui).
 */
export function mesDoCiclo(inicio: Date, agora = new Date()): number {
  if (agora.getTime() < inicio.getTime()) return 0;
  let meses =
    (agora.getUTCFullYear() - inicio.getUTCFullYear()) * 12 + (agora.getUTCMonth() - inicio.getUTCMonth());
  if (agora.getUTCDate() < inicio.getUTCDate()) meses -= 1;
  return Math.min(12, Math.max(1, meses + 1));
}

/** Conselheiro suspenso ou desligado perde o acesso às empresas pelo app. */
export function conselheiroComAcesso(status: string): boolean {
  return status === 'EM_FORMACAO' || status === 'CERTIFICADO';
}

/** Quem da empresa enxerga o Conselheiro no app: sócios (DONO conta) e responsáveis. */
export function papelComAcessoNaEmpresa(papel: string): boolean {
  return ehSocio(papel) || papel === 'RESPONSAVEL';
}

/* ───────────────────────── radar ───────────────────────── */

/**
 * Amplitude (maior − menor nota entre os sócios) a partir da qual um tema é
 * divergência. As notas por tema vêm de 5 perguntas de 1 a 5, então cada
 * degrau de resposta move 0,4: 2 pontos são 5 degraus — visões de fato
 * diferentes, não arredondamento.
 */
export const LIMIAR_DE_DIVERGENCIA = 2;

const umaCasa = (n: number) => Math.round(n * 10) / 10;

/**
 * Junta o autodiagnóstico dos sócios (dez notas por sócio, na ordem de
 * TEMAS): média, amplitude e os temas em que divergem, do mais ao menos
 * divergente. É o ponto de partida do conselheiro, não a nota final — essa
 * ele fecha com evidência.
 */
export function consolidarRadar(porSocio: number[][]): {
  media: number[];
  amplitude: number[];
  divergentes: number[];
} {
  if (porSocio.length === 0) {
    return { media: TEMAS.map(() => 0), amplitude: TEMAS.map(() => 0), divergentes: [] };
  }
  const media = TEMAS.map((_, t) => umaCasa(porSocio.reduce((s, n) => s + (n[t] ?? 0), 0) / porSocio.length));
  const amplitude = TEMAS.map((_, t) => {
    const vals = porSocio.map((n) => n[t] ?? 0);
    return umaCasa(Math.max(...vals) - Math.min(...vals));
  });
  const divergentes = amplitude
    .map((a, t) => ({ a, t }))
    .filter((x) => x.a >= LIMIAR_DE_DIVERGENCIA)
    .sort((x, y) => y.a - x.a || x.t - y.t)
    .map((x) => x.t);
  return { media, amplitude, divergentes };
}

export type MotivoRadarRecusado =
  | 'RADAR_ABERTO'
  | 'PARTIDA_JA_EXISTE'
  | 'FINAL_JA_EXISTE'
  | 'SEM_PARTIDA'
  | 'SEM_CICLO_ATIVO';

export const MENSAGEM_RADAR_RECUSADO: Record<MotivoRadarRecusado, string> = {
  RADAR_ABERTO: 'Já há um radar aberto neste ciclo. Feche-o antes de abrir outro.',
  PARTIDA_JA_EXISTE: 'O radar de partida deste ciclo já existe.',
  FINAL_JA_EXISTE: 'O radar final deste ciclo já existe.',
  SEM_PARTIDA: 'Feche o radar de partida antes de abrir um de marco ou o final.',
  SEM_CICLO_ATIVO: 'A empresa não tem ciclo ativo. O ciclo é aberto no HAOps.',
};

/**
 * Pode abrir um radar deste tipo no ciclo? Um aberto por vez; partida e
 * final uma vez por ciclo; marco e final só depois da partida fechada.
 */
export function motivoParaRecusarRadar(
  tipo: (typeof TIPOS_DE_RADAR)[number],
  doCiclo: { tipo: string; fechado: boolean }[],
  temCicloAtivo = true,
): MotivoRadarRecusado | null {
  if (!temCicloAtivo) return 'SEM_CICLO_ATIVO';
  if (doCiclo.some((r) => !r.fechado)) return 'RADAR_ABERTO';
  if (tipo === 'PARTIDA') return doCiclo.some((r) => r.tipo === 'PARTIDA') ? 'PARTIDA_JA_EXISTE' : null;
  if (!doCiclo.some((r) => r.tipo === 'PARTIDA' && r.fechado)) return 'SEM_PARTIDA';
  if (tipo === 'FINAL' && doCiclo.some((r) => r.tipo === 'FINAL')) return 'FINAL_JA_EXISTE';
  return null;
}

/* ───────────────────────── metas e indicadores ───────────────────────── */

export const NOME_DA_META: Record<(typeof TIPOS_DE_META)[number], string> = {
  FINANCEIRA: 'Financeira',
  OPERACIONAL: 'Operacional',
  PESSOAS: 'Pessoas',
  PESSOAL: 'Pessoal (do sócio)',
};

/**
 * Quanto do caminho da linha de base ao alvo já foi feito, de 0 a 1. Vale
 * para meta que sobe (faturamento) e que desce (prazo de entrega, churn).
 * Null quando falta um dos três números ou base e alvo são iguais.
 */
export function progressoDaMeta(m: {
  linha_base: number | null;
  alvo: number | null;
  atual: number | null;
}): number | null {
  const { linha_base: base, alvo, atual } = m;
  if (base == null || alvo == null || atual == null || base === alvo) return null;
  const f = (atual - base) / (alvo - base);
  return Math.max(0, Math.min(1, Math.round(f * 1000) / 1000));
}

/** Fora da faixa esperada do indicador: o conselheiro é alertado (escopo §6.3). */
export function foraDaFaixa(valor: number, min: number | null, max: number | null): boolean {
  return (min != null && valor < min) || (max != null && valor > max);
}

/** `2026-10` → primeiro dia do mês, em UTC; null se não for um mês válido. */
export function competenciaDoTexto(texto: string): Date | null {
  const m = /^(\d{4})-(\d{2})$/.exec(texto.trim());
  if (!m) return null;
  const ano = Number(m[1]);
  const mes = Number(m[2]);
  if (mes < 1 || mes > 12 || ano < 2000 || ano > 2100) return null;
  return new Date(Date.UTC(ano, mes - 1, 1));
}

/* ───────────────────────── ciclo mensal ───────────────────────── */

/** A empresa preenche até 5 dias antes da manhã (D-5), para dar tempo à preparação. */
export const DIAS_DE_ANTECEDENCIA_DO_PREENCHIMENTO = 5;

export function prazoDoPreenchimento(inicioDaManha: Date): Date {
  return new Date(inicioDaManha.getTime() - DIAS_DE_ANTECEDENCIA_DO_PREENCHIMENTO * 86_400_000);
}

export type EstadoDoPreenchimento = 'FEITO' | 'NO_PRAZO' | 'ATRASADO';

export function estadoDoPreenchimento(
  m: { preenchido_em: Date | null; preenchimento_ate: Date | null },
  agora = new Date(),
): EstadoDoPreenchimento {
  if (m.preenchido_em) return 'FEITO';
  if (m.preenchimento_ate && agora.getTime() > m.preenchimento_ate.getTime()) return 'ATRASADO';
  return 'NO_PRAZO';
}

/** A empresa pode propor mais um tema para esta manhã? (no máximo 3, §6.1) */
export function empresaPodeProporTema(temasDaEmpresaNaManha: number): boolean {
  return temasDaEmpresaNaManha < MAX_TEMAS_DA_EMPRESA;
}

/** Manhã que ainda aceita preenchimento, temas e mudança de pauta. */
export function manhaEmPreparacao(status: string): boolean {
  return status === 'AGENDADA' || status === 'PREPARACAO' || status === 'PAUTA_CONFIRMADA';
}

/* ───────────────────────── arquivos ───────────────────────── */

/**
 * Os arquivos do Conselheiro moram no bucket privado do HackAcademy, que o
 * HackOS administra (o Academy não tem credencial da AWS). ANEXO é o que a
 * empresa ou o conselheiro junta à manhã; GRAVACAO é o áudio da manhã.
 */
export const FINALIDADES_DE_ARQUIVO = ['ANEXO', 'GRAVACAO'] as const;
export type FinalidadeDeArquivo = (typeof FINALIDADES_DE_ARQUIVO)[number];

const MB = 1024 * 1024;
export const LIMITE_DO_ANEXO = 50 * MB;
/** 4 h de áudio a 128 kbps dão ~230 MB; 2 GB é folga para qualquer formato. */
export const LIMITE_DA_GRAVACAO = 2_000_000_000;

/** O que se anexa: documentos, planilhas, apresentações, imagens e áudio. */
const TIPOS_DE_ANEXO = [
  /^application\/pdf$/,
  /^image\/(png|jpeg|webp|gif)$/,
  /^text\/(plain|csv)$/,
  /^application\/(msword|vnd\.ms-excel|vnd\.ms-powerpoint)$/,
  /^application\/vnd\.openxmlformats-officedocument\.(wordprocessingml\.document|spreadsheetml\.sheet|presentationml\.presentation)$/,
  /^audio\//,
];
const TIPOS_DE_GRAVACAO = [/^audio\//];

export function motivoParaRecusarArquivo(a: { finalidade: FinalidadeDeArquivo; tipo: string; tamanho: number }): string | null {
  const tipos = a.finalidade === 'GRAVACAO' ? TIPOS_DE_GRAVACAO : TIPOS_DE_ANEXO;
  if (!tipos.some((t) => t.test(a.tipo))) {
    return a.finalidade === 'GRAVACAO'
      ? 'A gravação precisa ser um arquivo de áudio.'
      : 'Tipo de arquivo não aceito. Envie PDF, Word, Excel, PowerPoint, imagem, texto ou áudio.';
  }
  const limite = a.finalidade === 'GRAVACAO' ? LIMITE_DA_GRAVACAO : LIMITE_DO_ANEXO;
  if (a.tamanho <= 0) return 'Arquivo vazio.';
  if (a.tamanho > limite) return `Arquivo grande demais: o limite é ${Math.round(limite / MB)} MB.`;
  return null;
}

const TIPO_PELA_EXTENSAO: Record<string, string> = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
  gif: 'image/gif',
  txt: 'text/plain',
  csv: 'text/csv',
  doc: 'application/msword',
  xls: 'application/vnd.ms-excel',
  ppt: 'application/vnd.ms-powerpoint',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  mp3: 'audio/mpeg',
  m4a: 'audio/mp4',
  wav: 'audio/wav',
  ogg: 'audio/ogg',
  webm: 'audio/webm',
};

/**
 * O tipo do arquivo: o que o navegador disse, ou — quando ele não sabe, o que
 * acontece com .docx e .csv em vários sistemas — o da extensão.
 */
export function tipoDoArquivo(nome: string, tipoDoNavegador: string): string {
  const t = tipoDoNavegador.trim().toLowerCase();
  if (t && t !== 'application/octet-stream') return t;
  const ext = /\.([A-Za-z0-9]{1,8})$/.exec(nome)?.[1]?.toLowerCase() ?? '';
  return TIPO_PELA_EXTENSAO[ext] ?? (t || 'application/octet-stream');
}

/** O nome do arquivo como vai para a chave: sem acento, sem espaço, com a extensão. */
export function nomeSeguro(nome: string): string {
  const base = nome
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Za-z0-9._-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^[-.]+|[-.]+$/g, '');
  const ext = /\.[A-Za-z0-9]{1,8}$/.exec(base)?.[0] ?? '';
  const corpo = base.slice(0, base.length - ext.length).slice(0, 80).replace(/[-.]+$/g, '');
  return (corpo || 'arquivo') + ext.toLowerCase();
}

/** O caminho relativo no bucket; o HackOS põe o prefixo do ambiente na frente. */
export function caminhoDoArquivo(empresaId: string, arquivoId: string, nome: string): string {
  return `conselheiro/${empresaId}/${arquivoId}-${nomeSeguro(nome)}`;
}

/* ───────────────────────── a manhã (condução) ───────────────────────── */

/** Uma manhã do conselho dura umas 3 horas; o roteiro reparte esse tempo. */
export const DURACAO_PADRAO_DA_MANHA_MIN = 180;
/** Tema nenhum ganha menos que isto, mesmo com muitos na pauta. */
export const MINIMO_POR_TEMA_MIN = 20;

export type TipoDeBloco = 'ABERTURA' | 'NUMEROS' | 'DECISOES' | 'TEMA' | 'CONVERSA' | 'FECHAMENTO';

export type Bloco = {
  chave: string;
  tipo: TipoDeBloco;
  titulo: string;
  /** Tempo previsto. */
  minutos: number;
  tema_pauta_id?: string;
  /** Segundos já gastos em visitas anteriores ao bloco. */
  gasto_s: number;
  /** Quando a visita atual começou; null = o bloco não está aberto. */
  aberto_em: string | null;
};

const FIXOS: { tipo: TipoDeBloco; titulo: string; minutos: number }[] = [
  { tipo: 'ABERTURA', titulo: 'Abertura e presença', minutos: 10 },
  { tipo: 'NUMEROS', titulo: 'Números do mês', minutos: 25 },
  { tipo: 'DECISOES', titulo: 'Andamento das decisões', minutos: 20 },
];
const FECHAMENTO = { tipo: 'FECHAMENTO' as const, titulo: 'Fechamento', minutos: 20 };

/**
 * O roteiro em blocos: abertura, números, decisões, um bloco por tema da pauta
 * (na ordem confirmada) e fechamento. Os temas repartem o que sobra das 3 h.
 */
export function montarRoteiro(
  temas: { id: string; titulo: string }[],
  duracaoMin = DURACAO_PADRAO_DA_MANHA_MIN,
): Bloco[] {
  const fixos = FIXOS.reduce((s, b) => s + b.minutos, 0) + FECHAMENTO.minutos;
  const sobra = Math.max(0, duracaoMin - fixos);
  const novo = (b: Omit<Bloco, 'gasto_s' | 'aberto_em'>): Bloco => ({ ...b, gasto_s: 0, aberto_em: null });
  const meio = temas.length
    ? temas.map((t, i) =>
        novo({
          chave: `tema-${t.id}`,
          tipo: 'TEMA',
          titulo: `Tema ${i + 1}: ${t.titulo}`,
          minutos: Math.max(MINIMO_POR_TEMA_MIN, Math.floor(sobra / temas.length / 5) * 5),
          tema_pauta_id: t.id,
        }),
      )
    : [novo({ chave: 'conversa', tipo: 'CONVERSA', titulo: 'Conversa do mês', minutos: Math.max(MINIMO_POR_TEMA_MIN, sobra) })];
  return [
    ...FIXOS.map((b) => novo({ chave: b.tipo.toLowerCase(), ...b })),
    ...meio,
    novo({ chave: 'fechamento', ...FECHAMENTO }),
  ];
}

/** Fecha o bloco aberto (somando o tempo dele) e abre o pedido. Não muda o original. */
export function irParaBloco(roteiro: Bloco[], chave: string, agora: Date): Bloco[] {
  if (!roteiro.some((b) => b.chave === chave)) throw new Error('Bloco fora do roteiro.');
  return roteiro.map((b) => {
    const fechado =
      b.aberto_em != null
        ? { ...b, gasto_s: b.gasto_s + Math.max(0, Math.round((agora.getTime() - Date.parse(b.aberto_em)) / 1000)), aberto_em: null }
        : b;
    return fechado.chave === chave ? { ...fechado, aberto_em: agora.toISOString() } : fechado;
  });
}

/** Fecha o bloco aberto, sem abrir outro (fim da manhã). */
export function fecharRoteiro(roteiro: Bloco[], agora: Date): Bloco[] {
  return roteiro.map((b) =>
    b.aberto_em != null
      ? { ...b, gasto_s: b.gasto_s + Math.max(0, Math.round((agora.getTime() - Date.parse(b.aberto_em)) / 1000)), aberto_em: null }
      : b,
  );
}

/** O cronômetro de um bloco: quanto já foi, quanto estava previsto, se estourou. */
export function tempoDoBloco(b: Bloco, agora: Date): { decorrido_s: number; previsto_s: number; estourou: boolean } {
  const corrente = b.aberto_em ? Math.max(0, Math.round((agora.getTime() - Date.parse(b.aberto_em)) / 1000)) : 0;
  const decorrido_s = b.gasto_s + corrente;
  const previsto_s = b.minutos * 60;
  return { decorrido_s, previsto_s, estourou: decorrido_s > previsto_s };
}

/** mm:ss, ou h:mm:ss a partir de uma hora. */
export function relogio(segundos: number): string {
  const s = Math.max(0, Math.floor(segundos));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
}

/** Para que serve cada consentimento (§8): operação é o que permite gravar para a ata. */
export type FinalidadeDeConsentimento = (typeof FINALIDADES_DE_CONSENTIMENTO)[number];

/**
 * Pode gravar? Só com consentimento de OPERAÇÃO de todos os presentes. Quem
 * não está na sala não precisa consentir; quem está e não consentiu impede.
 */
export function podeGravar(
  participantes: { id: string; presente: boolean }[],
  consentiuOperacao: (participanteId: string) => boolean,
): { pode: boolean; faltam: string[] } {
  const faltam = participantes.filter((p) => p.presente && !consentiuOperacao(p.id)).map((p) => p.id);
  return { pode: participantes.some((p) => p.presente) && faltam.length === 0, faltam };
}

/** Nota da avaliação da manhã pelo sócio: 1 a 5. */
export function notaDeAvaliacaoValida(n: number): boolean {
  return Number.isInteger(n) && n >= 1 && n <= 5;
}

/* ───────────────────────── a manhã (gravação) ───────────────────────── */

/**
 * A gravação vai em fatias de 5 minutos, cada uma um arquivo de áudio
 * completo, enviada enquanto a manhã segue: se o aparelho travar, perde-se no
 * máximo a fatia em curso, e cada fatia cabe folgada no limite de um
 * transcritor (25 MB).
 */
export const DURACAO_DA_FATIA_S = 300;
/** Depois de encerrar, ainda se aceita a última fatia por este tempo (conexão lenta). */
export const JANELA_DA_ULTIMA_FATIA_MIN = 120;

/** Pode enviar uma fatia? Durante a manhã, ou logo depois de encerrada (a última). */
export function podeEnviarFatia(m: { status: string; fim_real: Date | null }, agora = new Date()): boolean {
  if (m.status === 'EM_ANDAMENTO') return true;
  return (
    m.status === 'REALIZADA' &&
    m.fim_real != null &&
    agora.getTime() - m.fim_real.getTime() <= JANELA_DA_ULTIMA_FATIA_MIN * 60_000
  );
}

/** Segundos gravados, somando as fatias (cada uma com início e fim). */
export function segundosGravados(fatias: { inicio_em: Date | string | null; fim_em: Date | string | null }[]): number {
  return Math.round(
    fatias.reduce((s, f) => {
      if (!f.inicio_em || !f.fim_em) return s;
      return s + Math.max(0, new Date(f.fim_em).getTime() - new Date(f.inicio_em).getTime()) / 1000;
    }, 0),
  );
}

/* ───────────────────────── a manhã (transcrição e ata) ───────────────────────── */

export type TrechoTranscrito = { inicio: number; fim: number; texto: string };

const horaBr = (d: Date) =>
  d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' });

/**
 * A transcrição da manhã inteira, em ordem: cada trecho com a hora em que foi
 * dito (o início da fatia mais o tempo dentro dela) e, onde a manhã ficou fora
 * da gravação, uma lacuna marcada — para a ata não inventar o que não ouviu.
 */
export function montarTranscricao(
  fatias: { inicio_em: Date; trechos: TrechoTranscrito[] }[],
  privados: { inicio_em: Date; fim_em: Date | null }[],
): string {
  const linhas: { t: number; texto: string }[] = [];
  for (const f of fatias) {
    for (const tr of f.trechos) {
      if (!tr.texto.trim()) continue;
      const quando = new Date(f.inicio_em.getTime() + tr.inicio * 1000);
      linhas.push({ t: quando.getTime(), texto: `[${horaBr(quando)}] ${tr.texto.trim()}` });
    }
  }
  for (const p of privados) {
    const fim = p.fim_em ? horaBr(p.fim_em) : '…';
    linhas.push({ t: p.inicio_em.getTime(), texto: `[${horaBr(p.inicio_em)}–${fim}] (fora da gravação)` });
  }
  return linhas
    .sort((a, b) => a.t - b.t)
    .map((l) => l.texto)
    .join('\n');
}

/* ───────────────────────── a manhã online (Google Meet) ───────────────────────── */

/** O que o HackOS devolve de `/meet/artefatos` (src/lib/conselheiro-meet.ts). */
export type TrechoDoMeet = { inicio: string; fim: string; falante: string; texto: string };
export type ArtefatosDoMeet = {
  conferencias: { id: string; inicio: string | null; fim: string | null }[];
  transcricoes: { id: string; estado: string; documento: string | null; trechos: TrechoDoMeet[] }[];
  gravacoes: { id: string; estado: string; arquivo: string | null; inicio: string | null; fim: string | null }[];
};

/** O Meet gera gravação e transcrição minutos (às vezes horas) depois da reunião. */
export const ESPERA_MAXIMA_DO_MEET_H = 24;
/** Sem nenhum artefato depois disto, ninguém gravou nem transcreveu. */
export const ESPERA_SEM_ARTEFATO_MIN = 45;
/** Reunião que segue aberta depois de a manhã ser encerrada no app: espera até isto. */
const ESPERA_DA_REUNIAO_ABERTA_H = 6;

/**
 * Já dá para trazer o que o Meet gerou? ESPERAR enquanto a reunião segue ou
 * algum arquivo ainda está sendo gerado (até 24 h); SEM_GRAVACAO se nada foi
 * gravado nem transcrito; IMPORTAR o que estiver pronto.
 */
export function oQueFazerComOMeet(a: ArtefatosDoMeet, fimReal: Date, agora: Date): 'ESPERAR' | 'IMPORTAR' | 'SEM_GRAVACAO' {
  const desde = agora.getTime() - fimReal.getTime();
  const H = 3_600_000;
  if (a.conferencias.some((c) => !c.fim) && desde < ESPERA_DA_REUNIAO_ABERTA_H * H) return 'ESPERAR';
  const artefatos = [...a.transcricoes, ...a.gravacoes];
  if (!artefatos.length) return desde < ESPERA_SEM_ARTEFATO_MIN * 60_000 ? 'ESPERAR' : 'SEM_GRAVACAO';
  const pendentes = artefatos.filter((x) => x.estado !== 'FILE_GENERATED');
  if (pendentes.length && desde < ESPERA_MAXIMA_DO_MEET_H * H) return 'ESPERAR';
  return artefatos.length > pendentes.length ? 'IMPORTAR' : 'SEM_GRAVACAO';
}

/**
 * A transcrição do Meet no mesmo formato da do app, com quem falou: falas
 * seguidas da mesma pessoa (menos de 1 minuto de intervalo) viram uma linha.
 */
export function transcricaoDoMeet(trechos: TrechoDoMeet[]): string {
  const ordem = [...trechos]
    .filter((t) => t.texto.trim())
    .sort((a, b) => Date.parse(a.inicio) - Date.parse(b.inicio));
  const linhas: { inicio: Date; ultimo: number; falante: string; texto: string[] }[] = [];
  for (const t of ordem) {
    const ini = Date.parse(t.inicio);
    const fim = Date.parse(t.fim) || ini;
    const ultima = linhas[linhas.length - 1];
    if (ultima && ultima.falante === t.falante && ini - ultima.ultimo < 60_000) {
      ultima.texto.push(t.texto.trim());
      ultima.ultimo = Math.max(ultima.ultimo, fim);
    } else {
      linhas.push({ inicio: new Date(ini), ultimo: fim, falante: t.falante, texto: [t.texto.trim()] });
    }
  }
  return linhas.map((l) => `[${horaBr(l.inicio)}] ${l.falante}: ${l.texto.join(' ')}`).join('\n');
}

export type DecisaoSugerida = { id: string; descricao: string; responsavel: string | null; prazo: string | null; motivo: string | null };
export type RespostaDaAta = { ata: string; resumo: string; decisoes_sugeridas: Omit<DecisaoSugerida, 'id'>[] };

/** O pedido à IA: quem estava, a pauta, o que já foi decidido e a transcrição. */
export function pedidoDaAta(d: {
  empresa: string;
  data: Date;
  participantes: { nome: string; papel: string }[];
  pauta: { titulo: string; origem: string }[];
  decisoes: { descricao: string; responsavel: string | null; prazo: Date | null }[];
  relato: string | null;
  transcricao: string;
}): { sistema: string; prompt: string } {
  const sistema = [
    'Você é o secretário de um conselho consultivo de uma empresa brasileira (programa Conselheiro, da Hack).',
    'Escreve em português do Brasil, com frases curtas e concretas, sem jargão e sem elogios.',
    'Use só o que está na transcrição e nos dados enviados. Não invente números, nomes, prazos nem decisões.',
    'Trechos "(fora da gravação)" não foram gravados: não comente o que pode ter sido dito ali.',
    'Linhas no formato "[HH:MM] Nome: fala" trazem quem falou (transcrição do Meet): atribua falas e compromissos a essas pessoas.',
    'Responda APENAS com um objeto JSON, sem texto antes ou depois, com as chaves:',
    '"ata": a ata da manhã em texto (seções: Presentes; Pauta; para cada tema, o que se discutiu e o que se concluiu; Decisões; Próximos passos),',
    '"resumo": um resumo para os sócios em até 8 linhas, em linguagem simples, com o que muda na prática,',
    '"decisoes_sugeridas": lista de decisões que aparecem na conversa e NÃO estão entre as já registradas, cada uma com "descricao", "responsavel" (nome, ou null), "prazo" (AAAA-MM-DD, ou null) e "motivo" (uma frase, ou null).',
  ].join('\n');
  const data = d.data.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' });
  const prompt = [
    `Empresa: ${d.empresa}`,
    `Data da manhã: ${data}`,
    `Presentes: ${d.participantes.map((p) => `${p.nome} (${p.papel})`).join(', ') || '—'}`,
    '',
    'Pauta:',
    ...(d.pauta.length ? d.pauta.map((t, i) => `${i + 1}. ${t.titulo}${t.origem === 'DESVIO' ? ' (surgiu na manhã)' : ''}`) : ['—']),
    '',
    'Decisões já registradas na manhã:',
    ...(d.decisoes.length
      ? d.decisoes.map(
          (x) =>
            `- ${x.descricao}${x.responsavel ? ` · ${x.responsavel}` : ''}${x.prazo ? ` · até ${x.prazo.toISOString().slice(0, 10)}` : ''}`,
        )
      : ['—']),
    '',
    `Relato do mês, escrito pela empresa antes da manhã:\n${d.relato?.trim() || '—'}`,
    '',
    'Transcrição (hora de Brasília):',
    d.transcricao.trim() || '(sem gravação: escreva a ata só com a pauta e as decisões registradas, e diga isso nela)',
  ].join('\n');
  return { sistema, prompt };
}

/** Lê a resposta da IA, tolerando cercas de código e texto em volta do JSON. */
export function lerRespostaDaAta(texto: string): RespostaDaAta {
  const inicio = texto.indexOf('{');
  const fim = texto.lastIndexOf('}');
  if (inicio < 0 || fim <= inicio) throw new Error('A IA não devolveu a ata no formato esperado.');
  let bruto: unknown;
  try {
    bruto = JSON.parse(texto.slice(inicio, fim + 1));
  } catch {
    throw new Error('A IA não devolveu a ata no formato esperado.');
  }
  const o = bruto as Record<string, unknown>;
  const txt = (v: unknown) => (typeof v === 'string' ? v.trim() : '');
  const ata = txt(o.ata);
  if (!ata) throw new Error('A IA devolveu a ata vazia.');
  const lista = Array.isArray(o.decisoes_sugeridas) ? o.decisoes_sugeridas : [];
  return {
    ata,
    resumo: txt(o.resumo),
    decisoes_sugeridas: lista
      .map((x) => x as Record<string, unknown>)
      .filter((x) => txt(x.descricao).length >= 5)
      .slice(0, 15)
      .map((x) => ({
        descricao: txt(x.descricao).slice(0, 1000),
        responsavel: txt(x.responsavel) || null,
        prazo: /^\d{4}-\d{2}-\d{2}$/.test(txt(x.prazo)) ? txt(x.prazo) : null,
        motivo: txt(x.motivo) || null,
      })),
  };
}

/** A gravação é apagada 90 dias depois de a ata ser validada (decisão de retenção, 02/10/2026). */
export const DIAS_DE_RETENCAO_DO_AUDIO = 90;

/* ───────────────────────── o dossiê de preparação (etapa 6) ───────────────────────── */

export type TemaDoDossie = {
  tema_pauta_id: string;
  /** O tema do método (0–9, índice de TEMAS) em que a IA enquadra o assunto, e por quê. */
  enquadramento: { tema: number | null; justificativa: string | null };
  hipoteses: string[];
  perguntas: string[];
  /** O que pedir ou olhar antes da manhã (números, documentos). */
  dados: string[];
};
export type Dossie = { panorama: string; alertas: string[]; divergencias: string[]; temas: TemaDoDossie[] };

const numeroBr = (n: number) => n.toLocaleString('pt-BR', { maximumFractionDigits: 2 });
const temaComForca = (t: number | null) =>
  t == null || !TEMAS[t] ? 'sem enquadramento' : `${TEMAS[t]} (${nomeDaForca(forcaDoTema(t)!)})`;

/** O pedido à IA: tudo o que o conselheiro olharia para preparar a manhã. */
export function pedidoDoDossie(d: {
  empresa: string;
  data: Date;
  tipo: string;
  relato: string | null;
  pauta: { titulo: string; contexto: string | null; origem: string; tema: number | null }[];
  radar: {
    tipo: string;
    data: Date;
    notas: number[] | null;
    media: number[] | null;
    amplitude: number[] | null;
    observacoes?: string | null;
  } | null;
  metas: { tipo: string; descricao: string; unidade: string | null; linha_base: number | null; alvo: number | null; atual: number | null }[];
  indicadores: {
    nome: string;
    unidade: string | null;
    tema: number | null;
    faixa_min: number | null;
    faixa_max: number | null;
    valores: { competencia: string; valor: number; fora_da_faixa: boolean }[];
  }[];
  decisoes: { descricao: string; responsavel: string | null; prazo: Date | null; status: string; atrasada: boolean }[];
  ultima: { data: Date; resumo: string | null } | null;
}): { sistema: string; prompt: string } {
  const sistema = [
    'Você prepara o conselheiro de um conselho consultivo de uma empresa brasileira (programa Conselheiro, da Hack) para a próxima manhã do conselho.',
    'O método organiza a empresa em 5 forças e 10 temas: ' +
      TEMAS.map((t, i) => `${i}=${t} (${nomeDaForca(forcaDoTema(i)!)})`).join(', ') +
      '.',
    'Escreve em português do Brasil, direto, sem elogios e sem jargão. Use só os dados enviados: não invente números, nomes nem fatos.',
    'Hipóteses de causa são hipóteses: escreva como possibilidades a testar, não como conclusões.',
    'Perguntas são para o conselheiro fazer aos sócios na manhã: abertas, curtas, que façam os sócios pensar e decidir.',
    'Responda APENAS com um objeto JSON, sem texto antes ou depois, com as chaves:',
    '"panorama": até 8 linhas sobre a situação da empresa e o que mudou desde a última manhã,',
    '"alertas": lista curta do que exige atenção (indicadores fora da faixa, decisões atrasadas, metas longe do alvo),',
    '"divergencias": lista curta dos temas em que os sócios enxergam a empresa de formas diferentes, cada um com uma pergunta para alinhar,',
    '"temas": uma entrada para cada item da pauta, com "item" (o número do item), "tema" (o número do tema do método, 0 a 9, ou null), "justificativa" (uma frase), "hipoteses" (2 a 4), "perguntas" (3 a 5) e "dados" (o que pedir ou olhar antes, 0 a 3).',
  ].join('\n');
  const data = d.data.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' });
  const faixa = (min: number | null, max: number | null) =>
    min == null && max == null ? '' : ` · faixa ${min == null ? '…' : numeroBr(min)}–${max == null ? '…' : numeroBr(max)}`;
  const radar = d.radar
    ? [
        `Radar mais recente (${d.radar.tipo}, ${d.radar.data.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}), nota de 0 a 10 por tema:`,
        ...TEMAS.map((t, i) => {
          const partes = [
            d.radar!.notas ? `conselheiro ${numeroBr(d.radar!.notas[i] ?? 0)}` : null,
            d.radar!.media ? `média dos sócios ${numeroBr(d.radar!.media[i] ?? 0)}` : null,
            d.radar!.amplitude && (d.radar!.amplitude[i] ?? 0) >= LIMIAR_DE_DIVERGENCIA
              ? `sócios divergem (${numeroBr(d.radar!.amplitude[i]!)} pontos entre a maior e a menor nota)`
              : null,
          ].filter(Boolean);
          return `- ${t}: ${partes.join(' · ') || '—'}`;
        }),
        ...(d.radar.observacoes?.trim() ? [`Observações do conselheiro no radar: ${d.radar.observacoes.trim()}`] : []),
      ]
    : ['Radar: ainda não há.'];
  const prompt = [
    `Empresa: ${d.empresa}`,
    `Próxima manhã: ${data} (${d.tipo})`,
    '',
    'Pauta (cada item precisa de uma entrada em "temas"):',
    ...(d.pauta.length
      ? d.pauta.map(
          (t, i) =>
            `${i + 1}. ${t.titulo} — ${temaComForca(t.tema)} · proposto por ${t.origem === 'EMPRESA' ? 'a empresa' : t.origem === 'CONSELHEIRO' ? 'o conselheiro' : t.origem.toLowerCase()}${t.contexto?.trim() ? `\n   Contexto: ${t.contexto.trim()}` : ''}`,
        )
      : ['— (pauta vazia: devolva "temas" vazio)']),
    '',
    `Relato do mês, escrito pela empresa:\n${d.relato?.trim() || '— (a empresa ainda não preencheu)'}`,
    '',
    ...radar,
    '',
    'Metas do ciclo:',
    ...(d.metas.length
      ? d.metas.map(
          (m) =>
            `- ${m.tipo}: ${m.descricao}${m.unidade ? ` (${m.unidade})` : ''} · base ${m.linha_base == null ? '—' : numeroBr(m.linha_base)} · alvo ${m.alvo == null ? '—' : numeroBr(m.alvo)} · atual ${m.atual == null ? '—' : numeroBr(m.atual)}`,
        )
      : ['—']),
    '',
    'Indicadores (últimos valores, do mais antigo ao mais recente):',
    ...(d.indicadores.length
      ? d.indicadores.map(
          (i) =>
            `- ${i.nome}${i.unidade ? ` (${i.unidade})` : ''} — ${temaComForca(i.tema)}${faixa(i.faixa_min, i.faixa_max)}: ${
              i.valores.length
                ? i.valores.map((v) => `${v.competencia.slice(0, 7)} ${numeroBr(v.valor)}${v.fora_da_faixa ? ' (FORA DA FAIXA)' : ''}`).join('; ')
                : 'sem valores'
            }`,
        )
      : ['—']),
    '',
    'Decisões em aberto:',
    ...(d.decisoes.length
      ? d.decisoes.map(
          (x) =>
            `- ${x.descricao} · ${x.responsavel ?? 'sem responsável'} · ${x.prazo ? `até ${x.prazo.toISOString().slice(0, 10)}` : 'sem prazo'} · ${x.atrasada ? 'ATRASADA' : x.status.toLowerCase().replace('_', ' ')}`,
        )
      : ['—']),
    '',
    d.ultima
      ? `Resumo da última manhã (${d.ultima.data.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}):\n${d.ultima.resumo?.trim() || '—'}`
      : 'Última manhã: esta é a primeira.',
  ].join('\n');
  return { sistema, prompt };
}

/**
 * Lê a resposta da IA. Cada item da pauta volta pelo número que tinha no
 * pedido; item que a IA inventou fica de fora, e listas longas são cortadas.
 */
export function lerRespostaDoDossie(texto: string, pautaIds: string[]): Dossie {
  const inicio = texto.indexOf('{');
  const fim = texto.lastIndexOf('}');
  const errado = () => new Error('A IA não devolveu o dossiê no formato esperado.');
  if (inicio < 0 || fim <= inicio) throw errado();
  let o: Record<string, unknown>;
  try {
    o = JSON.parse(texto.slice(inicio, fim + 1)) as Record<string, unknown>;
  } catch {
    throw errado();
  }
  const txt = (v: unknown, max = 600) => (typeof v === 'string' ? v.trim().slice(0, max) : '');
  const lista = (v: unknown, n: number) =>
    (Array.isArray(v) ? v : [])
      .map((x) => txt(x, 400))
      .filter((x) => x.length >= 3)
      .slice(0, n);
  const panorama = txt(o.panorama, 3000);
  if (!panorama) throw errado();
  const vistos = new Set<string>();
  const temas: TemaDoDossie[] = [];
  for (const bruto of Array.isArray(o.temas) ? o.temas : []) {
    const t = (bruto ?? {}) as Record<string, unknown>;
    const item = Number(t.item);
    const id = Number.isInteger(item) ? pautaIds[item - 1] : undefined;
    if (!id || vistos.has(id)) continue;
    vistos.add(id);
    const tema = Number(t.tema);
    temas.push({
      tema_pauta_id: id,
      enquadramento: {
        tema: t.tema != null && Number.isInteger(tema) && tema >= 0 && tema <= 9 ? tema : null,
        justificativa: txt(t.justificativa) || null,
      },
      hipoteses: lista(t.hipoteses, 5),
      perguntas: lista(t.perguntas, 6),
      dados: lista(t.dados, 4),
    });
  }
  // Na ordem da pauta.
  temas.sort((a, b) => pautaIds.indexOf(a.tema_pauta_id) - pautaIds.indexOf(b.tema_pauta_id));
  return { panorama, alertas: lista(o.alertas, 8), divergencias: lista(o.divergencias, 6), temas };
}
